import React, { useState, useEffect } from 'react';
import { Session, Feature, Component, Platform, BugUrl, ApiLink } from '../../types';
import { AppMessage } from '../../utils/messages';

interface AddBugModalProps {
  session: Session;
  allFeatures: Feature[];
  allComponents: Component[];
  bugLinks: ApiLink[];
  isOpen: boolean;
  onClose: () => void;
  onBugAdded: (bug: BugUrl) => Promise<void> | void;
  showMessage: (msg: AppMessage) => void;
  prefillData?: {
    featureId?: string;
    componentId?: string;
    platforms?: Platform[];
    testedPlatforms?: Platform[];
  };
  zIndex?: number;
}

const AddBugModal: React.FC<AddBugModalProps> = ({
  session,
  allFeatures,
  allComponents,
  bugLinks,
  isOpen,
  onClose,
  onBugAdded,
  showMessage,
  prefillData,
  zIndex = 1000
}) => {
  const [newBugUrl, setNewBugUrl] = useState('');
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>(prefillData?.featureId || '');
  const [selectedComponentId, setSelectedComponentId] = useState<string>(prefillData?.componentId || '');
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>(prefillData?.platforms || []);
  const [showCreateIssueSelector, setShowCreateIssueSelector] = useState(false);

  const sessionPlatforms = session.platforms || (session.platform ? [session.platform] : ['Windows']);

  // Reset form when modal opens/closes or prefillData changes
  useEffect(() => {
    if (isOpen) {
      setNewBugUrl('');
      setSelectedFeatureId(prefillData?.featureId || '');
      setSelectedComponentId(prefillData?.componentId || '');
      // Initialize with tested platforms if available, otherwise use platforms from prefillData
      setSelectedPlatforms(prefillData?.testedPlatforms || prefillData?.platforms || []);
      setShowCreateIssueSelector(false);
    }
  }, [isOpen, prefillData]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Prevent scrolling on body
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore original overflow when modal closes
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleAddBug = async () => {
    if (!newBugUrl.trim()) {
      showMessage({ type: 'error', text: 'Please enter a bug URL.' });
      return;
    }

    if (!selectedFeatureId) {
      showMessage({ type: 'error', text: 'Please select a feature for this bug.' });
      return;
    }

    if (!selectedComponentId) {
      showMessage({ type: 'error', text: 'Please select a component for this bug.' });
      return;
    }

    const trimmedUrl = newBugUrl.trim();
    const bugUrls = session.bugUrls || [];

    // Check for duplicates - only within the same component
    // Same URL is allowed on different components (bug might affect multiple components)
    const isDuplicate = bugUrls.some(bugUrl => {
      if (typeof bugUrl === 'string') return false; // Legacy format - can't check component
      const url = bugUrl.url;
      const bugComponentId = bugUrl.componentId || bugUrl.subDepId;
      // Check if URL matches AND component matches
      return url.toLowerCase() === trimmedUrl.toLowerCase() && 
             bugComponentId === selectedComponentId;
    });

    if (isDuplicate) {
      showMessage({ type: 'error', text: 'This bug URL already exists for this component. Duplicates are not allowed on the same component.' });
      return;
    }

    // Determine platforms to use
    let platformsToUse: Platform[] = [];
    if (selectedPlatforms.length > 0) {
      // Use explicitly selected platforms
      platformsToUse = selectedPlatforms;
    } else if (sessionPlatforms.length === 1) {
      // Single platform session - use it automatically
      platformsToUse = sessionPlatforms;
    } else if (prefillData?.testedPlatforms && prefillData.testedPlatforms.length > 0) {
      // Use tested platforms if available (when coming from component that was tested)
      platformsToUse = prefillData.testedPlatforms;
    } else {
      // Multiple platforms but none selected - require selection
      showMessage({ type: 'error', text: 'Please select at least one platform where this bug was found.' });
      return;
    }

    // Create BugUrl object
    const newBug: BugUrl = {
      url: trimmedUrl,
      featureId: selectedFeatureId,
      componentId: selectedComponentId,
      platforms: platformsToUse,
      createdAt: new Date().toISOString()
    };

    // Reset form and close modal immediately (optimistic UI update)
    // The parent component will handle the async save in the background
    setNewBugUrl('');
    setSelectedFeatureId(prefillData?.featureId || '');
    setSelectedComponentId(prefillData?.componentId || '');
    setSelectedPlatforms(prefillData?.testedPlatforms || prefillData?.platforms || []);
    onClose();
    
    // Call the callback to handle adding the bug (don't await - let it run in background)
    // The parent component updates state optimistically, so the UI is already updated
    const result = onBugAdded(newBug);
    if (result instanceof Promise) {
      result.catch((error: unknown) => {
        // Error handling is done in the parent component, but show a message if needed
        console.error('Error adding bug:', error);
        if (error instanceof Error) {
          showMessage({ type: 'error', text: error.message || 'Failed to add bug. Please try again.' });
        } else {
          showMessage({ type: 'error', text: 'Failed to add bug. Please try again.' });
        }
      });
    }
  };

  const handleCreateIssue = (link: ApiLink) => {
    window.open(link.url, '_blank', 'noopener,noreferrer');
    setShowCreateIssueSelector(false);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay" 
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        overflow: 'hidden',
        zIndex: zIndex,
        pointerEvents: 'auto'
      }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.preventDefault()}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
        <div className="card modal-content" onClick={(e) => e.stopPropagation()}>
          <h2 className="title-lg mb-md">Add Bug</h2>

        {/* Create Issue Button */}
        <div className="mb-md">
          <button
            className="btn btn-secondary btn-create-issue"
            onClick={() => setShowCreateIssueSelector(true)}
            title="Create GitHub issue"
          >
            <span>🔗</span>
            <span>Create Issue</span>
          </button>
          <div className="bug-url-hint-runner" style={{ fontSize: '0.875rem', color: '#666', fontStyle: 'italic', marginTop: '0.25rem' }}>
            Or paste the URL after creating the issue
          </div>
        </div>

        {/* Create Issue Selector */}
        {showCreateIssueSelector && (
          <div className="mb-md" style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <h3 className="mb-sm">Select Bug Tracking Link</h3>
            {bugLinks.length === 0 ? (
              <p style={{ fontSize: '0.875rem', color: '#666' }}>
                No bug links configured. Please add links in Settings → API/Links.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {bugLinks.map(link => (
                  <button
                    key={link.id}
                    className="btn modal-link-button"
                    onClick={() => handleCreateIssue(link)}
                  >
                    <span>{link.title}</span>
                    <span>→</span>
                  </button>
                ))}
              </div>
            )}
            <button
              className="btn btn-secondary mt-sm"
              onClick={() => setShowCreateIssueSelector(false)}
            >
              Cancel
            </button>
          </div>
        )}

        {/* URL Input */}
        <div className="mb-md">
          <label className="label">Bug URL <span className="text-danger">*</span></label>
          <input
            type="url"
            placeholder="https://github.com/..."
            value={newBugUrl}
            onChange={(e) => setNewBugUrl(e.target.value)}
            className="form-control"
            onKeyPress={(e) => e.key === 'Enter' && handleAddBug()}
          />
        </div>

        {/* Feature selector (required) */}
        <div className="mb-sm">
          <label className="label">Feature <span className="text-danger">*</span></label>
          <select
            value={selectedFeatureId}
            onChange={(e) => {
              setSelectedFeatureId(e.target.value);
              setSelectedComponentId(''); // Reset component when feature changes
              setSelectedPlatforms([]); // Reset platforms
            }}
            className="form-control"
            required
          >
            <option value="">Select a feature...</option>
            {allFeatures.map(feature => (
              <option key={feature.id} value={feature.id}>
                {feature.name}
              </option>
            ))}
          </select>
        </div>

        {/* Component selector (required) */}
        {selectedFeatureId && (
          <div className="mb-sm">
            <label className="label">Component <span className="text-danger">*</span></label>
            <select
              value={selectedComponentId}
              onChange={(e) => {
                setSelectedComponentId(e.target.value);
                setSelectedPlatforms([]); // Reset platforms when component changes
              }}
              className="form-control"
              required
            >
              <option value="">Select a component...</option>
              {allComponents
                .filter(c => c.featureId === selectedFeatureId)
                .map(component => (
                  <option key={component.id} value={component.id}>
                    {component.name}
                  </option>
                ))}
            </select>
          </div>
        )}

        {/* Platform selector - always show if component selected */}
        {selectedComponentId && (
          <div className="mb-md">
            <label className="label">Platforms Where Bug Was Found <span className="text-danger">*</span></label>
            <div className="platform-controls-group-runner">
              {sessionPlatforms.map(platform => {
                const isSelected = selectedPlatforms.includes(platform);
                return (
                  <div key={platform} className="platform-control-wrapper-runner">
                    <button
                      type="button"
                      className={`platform-tested-button ${isSelected ? 'tested' : ''}`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
                        } else {
                          setSelectedPlatforms([...selectedPlatforms, platform]);
                        }
                      }}
                      title={isSelected ? `${platform} selected (click to deselect)` : `Select ${platform}`}
                    >
                      <span className="platform-name">{platform}</span>
                      {isSelected && <span className="platform-icon">✓</span>}
                    </button>
                  </div>
                );
              })}
            </div>
            {sessionPlatforms.length === 1 && (
              <div style={{ fontSize: '0.875rem', color: '#666', fontStyle: 'italic', marginTop: '0.25rem' }}>
                Only one platform available for this session
              </div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAddBug}
            type="button"
            disabled={
              !newBugUrl.trim() || 
              !selectedFeatureId || 
              !selectedComponentId || 
              (sessionPlatforms.length > 1 && selectedPlatforms.length === 0 && (!prefillData?.testedPlatforms || prefillData.testedPlatforms.length === 0))
            }
          >
            Add Bug
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddBugModal;

